import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../Layouts/Sidebar';
import Topbar from '../Layouts/Topbar';
import './AllUsers.css';
import Swal from 'sweetalert2';

const base_url = 'https://api.homehive.co.zw';

const AllUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'landlords', 'tenants'
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${base_url}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log(response.data);
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load users',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // View user details
  const handleViewUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${base_url}/api/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      Swal.fire({
        title: 'User Details',
        html: `
          <div class="user-details">
            <p><strong>Name:</strong> ${response.data.user.name} ${response.data.user.surname}</p>
            <p><strong>Email:</strong> ${response.data.user.email}</p>
            <p><strong>Role:</strong> ${response.data.user.is_landlord ? 'Landlord' : 'Tenant'}</p>
            <p><strong>Status:</strong> ${response.data.user.is_active ? 'Active' : 'Inactive'}</p>
            <p><strong>Join Date:</strong> ${new Date(response.data.user.date_joined).toLocaleDateString()}</p>
            <p><strong>Phone:</strong> ${response.data.user.phone}</p>
            <p><strong>Subscription:</strong> ${response.data.user.is_subscribed ? 'Subscribed' : 'Not Subscribed'}</p>
            <p><strong>Subscription Expiry Date:</strong> ${response.data.user.subscription_end_date ? new Date(response.data.user.subscription_end_date).toLocaleDateString() : 'N/A'}</p>

          </div>
        `,
        confirmButtonColor: '#1a237e'
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load user details',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
    }
  };

  // Edit user
  const handleEditUser = async (user) => {
    try {
      const { value: formValues } = await Swal.fire({
        title: 'Edit User',
        html: `
          <input id="name" class="swal2-input" placeholder="Name" value="${user.name || ''}">
          <input id="email" class="swal2-input" placeholder="Email" value="${user.email || ''}">
          <select id="status" class="swal2-select">
            <option value="true" ${user.is_active ? 'selected' : ''}>Active</option>
            <option value="false" ${!user.is_active ? 'selected' : ''}>Inactive</option>
          </select>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonColor: '#1a237e',
        preConfirm: () => {
          return {
            name: document.getElementById('name').value,
            email: document.getElementById('email').value,
            is_active: document.getElementById('status').value === 'true'
          };
        }
      });

      if (formValues) {
        const token = localStorage.getItem('token');
        await axios.put(`${base_url}/api/users/${user.id}`, formValues, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        Swal.fire({
          title: 'Success',
          text: 'User updated successfully',
          icon: 'success',
          confirmButtonColor: '#1a237e'
        });

        fetchUsers(); // Refresh user list
      }
    } catch (error) {
      console.error('Error updating user:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to update user',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
    }
  };

  // Replace handleDeleteUser with handleToggleUserStatus
  const handleToggleUserStatus = async (user) => {
    try {
      const newStatus = !user.is_active;
      const action = newStatus ? 'activate' : 'deactivate';
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Do you want to ${action} this user's account?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: newStatus ? '#4caf50' : '#f44336',
        cancelButtonColor: '#999',
        confirmButtonText: `Yes, ${action} account!`
      });

      if (result.isConfirmed) {
        const token = localStorage.getItem('token');
        await axios.patch(`${base_url}/api/users/${user.id}/`, 
          {
            is_active: newStatus
          }, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        Swal.fire({
          title: 'Success',
          text: `User account has been ${action}d`,
          icon: 'success',
          confirmButtonColor: '#1a237e'
        });

        fetchUsers(); // Refresh user list
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to update user status',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
    }
  };

  // Filter users based on selected filter and search term
  useEffect(() => {
    let filtered = users;

    // Apply role filter
    if (filter !== 'all') {
      filtered = filtered.filter(user => user.is_landlord === (filter === 'landlord'));
    }

    // Apply search filter
    if (searchTerm) {
      
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
      );
    }

    setFilteredUsers(filtered);
  }, [filter, searchTerm, users]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const getStatusColor = (is_active) => {
    return is_active ? '#4caf50' : '#f44336';
  };

  const getRoleColor = (is_landlord) => {
    return is_landlord ? '#1a237e' : '#ff9800';
  };

  return (
    <div className="admin-users">
      <Sidebar />
      <Topbar />
      
      <div style={{marginTop: '-50px'}} className="users-content">
        <div className="users-header">
          <p>Manage and monitor all users in the system</p>
        </div>

        <div className="users-filters">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>

          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterChange('all')}
            >
              All Users
            </button>
            <button
              className={`filter-btn ${filter === 'landlord' ? 'active' : ''}`}
              onClick={() => handleFilterChange('landlord')}
            >
              Landlords
            </button>
            <button
              className={`filter-btn ${filter === 'tenant' ? 'active' : ''}`}
              onClick={() => handleFilterChange('tenant')}
            >
              Tenants
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Join Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-info">
                        <img
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || '')}&background=random`}
                          alt={user.name}
                          className="user-avatar"
                        />
                        <span>{user.name}</span>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span
                        className="role-badge"
                        style={{ backgroundColor: getRoleColor(user.is_landlord) }}
                      >
                        {user.is_landlord ? 'Landlord' : 'Tenant'}
                      </span>
                    </td>
                    <td>
                      <span
                        className="role-badge"
                        style={{ backgroundColor: getStatusColor(user.is_active) }}
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{new Date(user.date_joined).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="action-btn view"
                          onClick={() => handleViewUser(user.id)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        
                        <button 
                          className={`action-btn ${user.is_active ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleUserStatus(user)}
                          title={`${user.is_active ? 'Deactivate' : 'Activate'} Account`}
                          style={{ backgroundColor: user.is_active ? '#f44336' : '#4caf50' }}
                        >
                          <i className={`fas fa-${user.is_active ? 'ban' : 'check-circle'}`}></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllUsers;
